import { useEffect, useRef } from 'react';
import { useLocation, useNavigationType } from 'react-router-dom';

const scrollPositions = new Map<string, number>();

/**
 * Saves and restores scroll position for a scrollable container across route changes.
 *
 * Save: captures on click (capture phase) and popstate — both fire
 * before React Router processes navigation, so the position is correct.
 *
 * Restore: only on POP navigation (browser back/forward), not on PUSH
 * (clicking links). Uses a MutationObserver to wait for async content.
 */
export function useScrollRestoration(scrollRef: React.RefObject<HTMLElement | null>) {
	const { pathname } = useLocation();
	const navigationType = useNavigationType();
	const prevPathname = useRef(pathname);
	const pendingRestore = useRef<number | null>(null);

	// Save scroll position on click (before navigation) and popstate (before back/forward)
	useEffect(() => {
		const el = scrollRef.current;
		if (!el) return;

		const save = () => {
			scrollPositions.set(pathname, el.scrollTop);
		};

		document.addEventListener('click', save, { capture: true, passive: true });
		window.addEventListener('popstate', save);

		return () => {
			document.removeEventListener('click', save, { capture: true });
			window.removeEventListener('popstate', save);
		};
	}, [pathname, scrollRef]);

	// Watch for DOM changes and restore when content is tall enough
	useEffect(() => {
		const el = scrollRef.current;
		if (!el) return;

		const tryRestore = () => {
			const target = pendingRestore.current;
			if (target == null) return;

			const maxScroll = el.scrollHeight - el.clientHeight;
			if (maxScroll >= target) {
				el.scrollTop = target;
				pendingRestore.current = null;
			}
		};

		tryRestore();

		const observer = new MutationObserver(tryRestore);
		observer.observe(el, { childList: true, subtree: true });

		return () => observer.disconnect();
	}, [pathname, scrollRef]);

	// Restore or reset scroll on route change
	useEffect(() => {
		const el = scrollRef.current;
		if (!el) return;

		const prev = prevPathname.current;
		if (pathname === prev) return;
		prevPathname.current = pathname;

		// Only restore on back/forward navigation
		if (navigationType === 'POP') {
			const saved = scrollPositions.get(pathname);
			if (saved) {
				const maxScroll = el.scrollHeight - el.clientHeight;
				if (maxScroll >= saved) {
					el.scrollTop = saved;
					pendingRestore.current = null;
				} else {
					pendingRestore.current = saved;
				}
				return;
			}
		}

		el.scrollTop = 0;
		pendingRestore.current = null;
	}, [pathname, navigationType, scrollRef]);
}
